// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngAppProvider from "@awc/App/Components/XngAppProvider/XngAppProvider";
import { hideElementOnPrint, xScreen } from "@awc/XDL/Utilities";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { ReactNode } from "react";
import styled, { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
}

// XNG-Changes
export interface PageLayoutStyles {
    breadCrumbsWrapper?: InjectableCss;
}

// XNG-Changes: add styling
export const pageLayoutStyles: PageLayoutStyles = {
    breadCrumbsWrapper: {
        css: css`
            padding: 16px 33px 0;
            min-height: 48px;
            max-height: 48px;
            ${xScreen("lte", "lg")} {
                padding: 16px 16px 0;
            }
            ${xScreen("lte", "sm")} {
                padding: 16px 8px 0;
            }
            ${hideElementOnPrint}
        `,
    },
};

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
}: PageLayoutProps) => {
    return (
        <PageLayoutWrapper>
            {/* XNG-Changes: add XngAppProvider */}
            <XngAppProvider>
                {showHeader && header}
                {/* XNG-Change: will not have sticky header */}
                {/* {showCompactHeader && compactHeader} */}
                {showBreadcrumbs && (
                    // XNG-Changes: apply styling
                    <Page as="div" {...pageLayoutStyles.breadCrumbsWrapper}>
                        <PageBreadcrumbs />
                    </Page>
                )}
                {pageContent}
                {showFooter && footer}
                {showSignInOverlay && signInOverlay}
            </XngAppProvider>
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    // XNG-Change override the style of delayedSpinner as it has a 250px height
    &&& > div:first-child {
        height: 178px;
        ${xScreen("lte", "sm")} {
            height: 128px;
        }
    }
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default PageLayout;
